import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { PermissionITF, PERMISSION_DEFAULTS } from '@/authProvider'
import { RootStateITF } from '@store/redux-types'
import { UserITF } from '@/types'

/**
 * PermissionITF with every key resolved to a definite boolean (missing keys
 * fall back to PERMISSION_DEFAULTS / false at runtime).
 */
export type ResolvedPermissionsITF = { [K in keyof PermissionITF]-?: NonNullable<PermissionITF[K]> }

/**
 * Returns the full permissions object from Redux, layered over
 * PERMISSION_DEFAULTS so every known permission key reads as a boolean
 * (never undefined) and callers can pass values straight to boolean props.
 */
export const usePermissions = (): ResolvedPermissionsITF => {
  const perms = useSelector((state: RootStateITF) => state.auth.permissions)
  return useMemo(() => ({ ...PERMISSION_DEFAULTS, ...perms } as ResolvedPermissionsITF), [perms])
}

/**
 * Returns `true` if the current user has the given permission key.
 */
export const useHasPermission = (key: keyof PermissionITF | string): boolean => {
  const perms = usePermissions()
  return !!perms[key]
}

/**
 * Returns true when the logged-in user is an Activate-internal admin.
 */
export const useIsAcAdmin = (): boolean => {
  const user = useSelector((state: { profile: { user?: UserITF } }) => state.profile?.user)
  return user?.IsAcAdmin === true
}

/**
 * Checks multiple permissions at once.
 * @param keys – permission keys to check
 * @param mode – 'any' requires at least one; 'all' requires every key
 */
export const useHasPermissions = (
  keys: (keyof PermissionITF | string)[],
  mode: 'any' | 'all' = 'any'
): boolean => {
  const perms = usePermissions()
  if (keys.length === 0) return true
  return mode === 'any' ? keys.some((key) => !!perms[key]) : keys.every((key) => !!perms[key])
}
